import React from 'react';
import { isWeekend, isHoliday } from '../utils/holidays';
import * as XLSX from 'xlsx';

const AUTO_G_PER_KM = 170;
const OV_G_PER_KM = 14;
const KCAL_PER_KM_FIETS = 35;

function telMaand(year, month, registrations, userSettings) {
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const monthRegs = registrations[monthKey] || {};
  const werkdagen = userSettings.werkdagen ?? [1, 2, 3, 4, 5];
  const defaultThuiswerkDagen = userSettings.defaultThuiswerkDagen || [];
  const fietsKm = parseFloat(userSettings.kmPerRitFiets) || 0;
  const autoKm = parseFloat(userSettings.kmPerRitAuto) || 0;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  let ov = 0, fiets = 0, auto = 0, thuis = 0, vrij = 0, ziek = 0, nietIngevuld = 0, totaalWerkdagen = 0;
  let co2Gram = 0, kcal = 0;

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(Date.UTC(year, month, day));
    if (isWeekend(date) || isHoliday(date, year)) continue;
    const dayNum = date.getDay();
    if (!werkdagen.includes(dayNum)) continue;

    totaalWerkdagen++;
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isExplicit = monthRegs[dateString] !== undefined;
    const customDefault = (userSettings.defaultRegistratieDagen || {})[dayNum];
    const defaultDag = customDefault !== undefined ? customDefault : (defaultThuiswerkDagen.includes(dayNum) ? 'thuis' : '');
    const reg = isExplicit ? monthRegs[dateString] : defaultDag;

    if (reg === 'ov') { ov++; if (autoKm > 0) co2Gram += autoKm * 2 * (AUTO_G_PER_KM - OV_G_PER_KM); }
    else if (reg === 'fiets') { fiets++; if (fietsKm > 0) { co2Gram += fietsKm * 2 * AUTO_G_PER_KM; kcal += fietsKm * 2 * KCAL_PER_KM_FIETS; } }
    else if (reg === 'auto') auto++;
    else if (reg === 'thuis') thuis++;
    else if (reg === 'vrij') vrij++;
    else if (reg === 'ziek') ziek++;
    else nietIngevuld++;
  }

  return { ov, fiets, auto, thuis, vrij, ziek, nietIngevuld, totaalWerkdagen, co2Kg: co2Gram / 1000, kcal };
}

function YearOverview({ year, registrations, userSettings }) {
  const maandData = Array.from({ length: 12 }, (_, m) => ({
    maand: m,
    ...telMaand(year, m, registrations, userSettings),
  }));

  const totaal = maandData.reduce((acc, m) => ({
    ov: acc.ov + m.ov,
    fiets: acc.fiets + m.fiets,
    auto: acc.auto + m.auto,
    thuis: acc.thuis + m.thuis,
    vrij: acc.vrij + m.vrij,
    ziek: acc.ziek + m.ziek,
    nietIngevuld: acc.nietIngevuld + m.nietIngevuld,
    totaalWerkdagen: acc.totaalWerkdagen + m.totaalWerkdagen,
    co2Kg: acc.co2Kg + m.co2Kg,
    kcal: acc.kcal + m.kcal,
  }), { ov: 0, fiets: 0, auto: 0, thuis: 0, vrij: 0, ziek: 0, nietIngevuld: 0, totaalWerkdagen: 0, co2Kg: 0, kcal: 0 });

  const heeftCo2 = totaal.co2Kg > 0;
  const heeftKcal = totaal.kcal > 0;

  const downloadExcel = () => {
    const extraHeaders = [...(heeftKcal ? ['Kcal verbrand'] : []), ...(heeftCo2 ? ['CO₂ bespaard (kg)'] : [])];
    const headers = ['Maand', 'Werkdagen', 'OV', 'Fiets', 'Auto', 'Thuis', 'Vrij', 'Ziek', 'Niet ingevuld', ...extraHeaders];
    const rows = maandData.map(m => [
      new Date(year, m.maand).toLocaleDateString('nl-NL', { month: 'long' }),
      m.totaalWerkdagen, m.ov, m.fiets, m.auto, m.thuis, m.vrij, m.ziek, m.nietIngevuld,
      ...(heeftKcal ? [Math.round(m.kcal)] : []),
      ...(heeftCo2 ? [parseFloat(m.co2Kg.toFixed(2))] : []),
    ]);
    rows.push([
      'Totaal', totaal.totaalWerkdagen, totaal.ov, totaal.fiets, totaal.auto, totaal.thuis, totaal.vrij, totaal.ziek, totaal.nietIngevuld,
      ...(heeftKcal ? [Math.round(totaal.kcal)] : []),
      ...(heeftCo2 ? [parseFloat(totaal.co2Kg.toFixed(2))] : []),
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = Array(headers.length).fill({ wch: 16 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Jaaroverzicht');
    XLSX.writeFile(wb, `Jaaroverzicht_${year}.xlsx`);
  };

  const Cel = ({ val, kleur }) => (
    <td className="px-4 py-3 text-center text-sm">
      {val > 0 ? <span className={`inline-block min-w-[2rem] px-2 py-0.5 rounded-full font-semibold text-xs ${kleur}`}>{val}</span> : <span className="text-gray-300">—</span>}
    </td>
  );

  const kolommen = [
    { key: 'ov',    label: 'OV',    kleur: 'bg-green-100 text-green-800' },
    { key: 'fiets', label: 'Fiets', kleur: 'bg-orange-100 text-orange-800' },
    { key: 'auto',  label: 'Auto',  kleur: 'bg-purple-100 text-purple-800' },
    { key: 'thuis', label: 'Thuis', kleur: 'bg-blue-100 text-blue-800' },
    { key: 'vrij',  label: 'Vrij',  kleur: 'bg-yellow-100 text-yellow-800' },
    { key: 'ziek',  label: 'Ziek',  kleur: 'bg-red-100 text-red-800' },
  ];

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full">
      <div className="bg-gradient-to-r from-blue-700 to-blue-800 px-6 py-8 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-white">Jaaroverzicht {year}</h2>
          {(heeftCo2 || heeftKcal) && (
            <div className="flex items-center gap-4 mt-1.5">
              {heeftCo2 && (
                <span className="text-sm text-green-300">
                  {totaal.co2Kg.toFixed(1)} kg CO₂ bespaard
                </span>
              )}
              {heeftKcal && (
                <span className="text-sm text-orange-300">
                  {Math.round(totaal.kcal).toLocaleString('nl-NL')} kcal verbrand
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={downloadExcel}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 hover:bg-white/20 text-white transition"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download Excel
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="sticky top-0 z-10 bg-gray-100 px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Maand</th>
              <th className="sticky top-0 z-10 bg-gray-100 px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Werkdagen</th>
              {kolommen.map(k => (
                <th key={k.key} className="sticky top-0 z-10 bg-gray-100 px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">{k.label}</th>
              ))}
              <th className="sticky top-0 z-10 bg-gray-100 px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">Niet ingevuld</th>
              {heeftKcal && <th className="sticky top-0 z-10 bg-orange-50 px-4 py-3 text-center text-xs font-semibold text-orange-500 uppercase tracking-wide">Kcal</th>}
              {heeftCo2 && <th className="sticky top-0 z-10 bg-green-50 px-4 py-3 text-center text-xs font-semibold text-green-600 uppercase tracking-wide">CO₂ bespaard</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {maandData.map((m) => (
              <tr key={m.maand} className="hover:bg-gray-50 transition">
                <td className="px-6 py-3 text-sm font-medium text-gray-900">
                  {new Date(year, m.maand).toLocaleDateString('nl-NL', { month: 'long' })}
                </td>
                <td className="px-4 py-3 text-center text-sm text-gray-500 font-medium">{m.totaalWerkdagen}</td>
                {kolommen.map(k => (
                  <Cel key={k.key} val={m[k.key]} kleur={k.kleur} />
                ))}
                <td className="px-4 py-3 text-center text-sm">
                  {m.nietIngevuld > 0
                    ? <span className="inline-block min-w-[2rem] px-2 py-0.5 rounded-full font-semibold text-xs bg-gray-100 text-gray-500">{m.nietIngevuld}</span>
                    : <span className="text-gray-300">—</span>
                  }
                </td>
                {heeftKcal && (
                  <td className="px-4 py-3 text-center text-sm">
                    {m.kcal > 0
                      ? <span className="text-orange-600 font-medium">{Math.round(m.kcal).toLocaleString('nl-NL')}</span>
                      : <span className="text-gray-300">—</span>
                    }
                  </td>
                )}
                {heeftCo2 && (
                  <td className="px-4 py-3 text-center text-sm">
                    {m.co2Kg > 0
                      ? <span className="text-green-700 font-medium">{m.co2Kg.toFixed(1)} kg</span>
                      : <span className="text-gray-300">—</span>
                    }
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold">
              <td className="px-6 py-3 text-sm font-bold text-gray-900">Totaal</td>
              <td className="px-4 py-3 text-center text-sm font-bold text-gray-700">{totaal.totaalWerkdagen}</td>
              {kolommen.map(k => (
                <td key={k.key} className="px-4 py-3 text-center text-sm">
                  {totaal[k.key] > 0
                    ? <span className={`inline-block min-w-[2rem] px-2 py-0.5 rounded-full font-bold text-xs ${k.kleur}`}>{totaal[k.key]}</span>
                    : <span className="text-gray-300">—</span>
                  }
                </td>
              ))}
              <td className="px-4 py-3 text-center text-sm">
                {totaal.nietIngevuld > 0
                  ? <span className="inline-block min-w-[2rem] px-2 py-0.5 rounded-full font-bold text-xs bg-gray-100 text-gray-500">{totaal.nietIngevuld}</span>
                  : <span className="text-gray-300">—</span>
                }
              </td>
              {heeftKcal && (
                <td className="px-4 py-3 text-center text-sm font-bold text-orange-600">
                  {Math.round(totaal.kcal).toLocaleString('nl-NL')}
                </td>
              )}
              {heeftCo2 && (
                <td className="px-4 py-3 text-center text-sm font-bold text-green-700">
                  {totaal.co2Kg.toFixed(1)} kg
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default YearOverview;
